import { useCallback, useEffect, useRef, useState } from 'react';

import { useHistory, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';

import { Form } from 'features/common/components/Form';
import { PunchupPageShell } from 'features/common/components/Punchup/pages/PunchupPageShell';
import { PunchupPageContent } from 'features/common/components/Punchup/primitives/layout/PunchupPageContent';
import { PunchupPageHeader } from 'features/common/components/Punchup/primitives/layout/PunchupPageHeader';
import { PunchupErrorState } from 'features/common/components/Punchup/primitives/feedback/PunchupErrorState';
import { PunchupRadioCards } from 'features/common/components/Punchup/primitives/forms/fields/PunchupRadioCardGroup';
import { useGuestListUpload } from 'features/PunchupPages/CompedTickets/SendCompedTicketsModal';
import { postToast } from 'features/common/utils/postFlash';
import { useEventCapacity } from 'features/common/hooks/useEventCapacity';
import { ROUTES } from 'features/routes';
import { IdParamsType } from 'utils/types';

import { getEvent, invalidateAfterSend } from './services';
import {
	calculateTotalPrice,
	handleSubmit,
	handleSubmitSeated,
	initialFormData,
	poolExceededMessage,
	validateBulkBeforeSend,
	validationSchema,
} from './utils';
import { CompIssued, RecipientProps, RecipientTicketType } from './types';
import {
	buildSendPoolBudget,
	getExceededPools,
	poolItemsFromBulkRows,
	poolItemsFromRecipients,
	sumRequestedByPool,
	PoolRequestItem,
	SendPoolBudget,
} from './poolBudget';
import { useAvailabilityAndSend } from './hooks/useAvailabilityAndSend';
import { useRecentComps } from './hooks/useRecentComps';
import { PunchupErrorModal } from './components/PunchupErrorModal/PunchupErrorModal';
import { PunchupSendTicketsConfirmation } from './components/PunchupSendTicketsConfirmation';
import { PunchupSendTicketsForm } from './components/PunchupSendTicketsForm';
import { PunchupSendTicketsFormSeated } from './components/PunchupSendTicketsFormSeated';
import { PunchupSendTicketsTitleSection } from './components/PunchupSendTicketsForm/components/PunchupSendTicketsTitleSection';
import { PunchupRecipientInfo } from './components/PunchupRecipientInfo';
import { PunchupBulkUploadPanel } from './components/PunchupBulkUploadPanel';
import { PunchupBulkConfirmation } from './components/PunchupBulkConfirmation';
import { TicketAvailabilityTiles } from './components/TicketAvailabilityTiles';
import { SendTicketsPoolTiles } from './components/SendTicketsPoolTiles';
import { SendTicketsRecentComps } from './components/SendTicketsRecentComps';

import styles from './PunchupSendTickets.module.css';

// 'one'   — single recipient by quantity (PunchupSendTicketsForm; non-seated only)
// 'bulk'  — guest-list CSV/table (non-seated only)
// 'seats' — single recipient by exact chart selection (PunchupSendTicketsFormSeated; the sole seated mode)
type SendMode = 'one' | 'bulk' | 'seats';

// The one over-reach derivation both the on-send gate (needs pool names for the toast) and the live red
// ring (needs only the ids) share — `getExceededPools(sumRequestedByPool(items))`. Keeping it in one place
// means the ring and the gate can never disagree about which pools are over capacity.
const exceededPoolsFor = (items: PoolRequestItem[], budget: SendPoolBudget) =>
	getExceededPools(sumRequestedByPool(items), budget);
const exceededPoolIdSet = (items: PoolRequestItem[], budget: SendPoolBudget) =>
	new Set(exceededPoolsFor(items, budget).map((p) => p.poolId));

export const PunchupSendTickets = () => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	// Bumped after every successful send. The seated chart takes it as a prop (its selection lives
	// in useSeatsSelection, outside RHF); the non-seated form takes it as a KEY, because its price
	// slots are seeded in a mount-only effect and formMethods.reset() empties them — see below.
	const [formResetSignal, setFormResetSignal] = useState(0);
	const [mode, setMode] = useState<SendMode>('one');

	const { push } = useHistory();
	const { id } = useParams<IdParamsType>();
	const formMethods = useForm({
		defaultValues: initialFormData,
		resolver: yupResolver(validationSchema(t)),
	});

	const goToCompedTickets = () => push(ROUTES.COMPED_TICKETS.replace(':id', id));

	// Distinct from useGetEvent's ['event', id] (raw EventData) — this fetches the SAME `events/${id}`
	// endpoint but maps it to the send-specific SendTicketsEventProps (formatted dates, seats category
	// keys, re-shaped ticket types), so it needs its own honest key rather than colliding two queryFns
	// under one key. Residual: this is a second fetch of the same endpoint (the shapes don't map cleanly
	// from useGetEvent's camelized EventData, so they are not deduped here).
	const {
		data: event,
		refetch,
		isError,
	} = useQuery({
		queryKey: ['sendTicketsEvent', id],
		queryFn: () => getEvent(id),
		enabled: !!id,
	});

	const isSeated = !!event?.seatsEventKey;

	// Shared-capacity pools (non-seated only — seated events use seats.io, not pools). Drives the
	// read-only pool tiles, the live stepper clamp, and the on-send pool gate. Derived from the
	// shared useEventCapacity source so "remaining" matches the dashboard + analytics exactly.
	const { pools, types } = useEventCapacity();
	const poolBudget = buildSendPoolBudget(pools, types);
	const hasPools = poolBudget.hasPools && !isSeated;
	// A seated event has exactly one send mode — chart selection. Resolve to it once the event loads
	// (the API has no quantity/auto-assign path for seated tickets, so the former "Single" mode is gone).
	// Only fires on the false→true transition.
	useEffect(() => {
		if (isSeated) setMode('seats');
	}, [isSeated]);

	// RHF returns the watched value loosely typed; narrow it once here to the project's RecipientProps so every
	// downstream read (total price, quantity predicates, confirmation list) is typed, not `any`.
	const recipientData = (formMethods.watch('recipients') ?? []) as RecipientProps[];
	// Explicit subscription so setValue('recipients[0].ticketTypes', ...) in the seated child
	// triggers confirmation re-render; cast bypasses RHF's dot-only path types.
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const _seatedTicketTypesWatcher =
		mode === 'seats' ? (formMethods.watch as (path: string) => unknown)('recipients[0].ticketTypes') : undefined;
	const totalPrice = calculateTotalPrice(recipientData);

	const firstRecipientTypes = recipientData[0]?.ticketTypes ?? [];
	// Seated picks exact seats → a non-empty selection IS the quantity. Single/bulk count quantities.
	const hasSeatedSelection = firstRecipientTypes.length > 0;
	const hasQuantitySum =
		firstRecipientTypes.reduce((sum: number, tt: RecipientTicketType) => sum + (tt.quantity ?? 0), 0) > 0;
	const hasQuantity = mode === 'seats' ? hasSeatedSelection : hasQuantitySum;
	const formSendDisabled = !recipientData[0] || !hasQuantity;

	const [justSentIds, setJustSentIds] = useState<number[]>([]);

	// The clear needs every input store, including the bulk hook created below — hence the
	// indirection rather than a forward reference. See CLAUDE.md invariant 8.
	const afterSendRef = useRef<(issued: CompIssued[]) => void>(() => undefined);
	const notifySendSucceeded = useCallback((issued: CompIssued[] = []) => afterSendRef.current(issued), []);

	const { mutateCheckAvailability, isSending, isErrorModalOpen, closeErrorModal } = useAvailabilityAndSend({
		formMethods,
		t,
		id,
		refetch,
		onSuccess: notifySendSucceeded,
	});

	const submitHandler = mode === 'seats' ? handleSubmitSeated : handleSubmit;

	const bulkUpload = useGuestListUpload(id, {
		onSuccess: (issued) => {
			invalidateAfterSend(queryClient, id);
			notifySendSucceeded(issued);
		},
		onError: () => postToast('error', t('send-tickets-error')),
		onLoadWarnings: (issues) => {
			// Guard empty issues so the handler is safe on its own — an empty join would otherwise toast a
			// content-free "File loaded with issues: ." (the hook only calls back when issues exist, but the
			// page handler should not depend on that).
			if (issues.length === 0) return;
			postToast('error', `${t('send-tickets-bulk-load-warning', 'File loaded with issues')}: ${issues.join('; ')}.`);
		},
	});

	const handleSendSuccess = (issued: CompIssued[]) => {
		setJustSentIds(issued.map((comp) => comp.transactionId));
		formMethods.reset(initialFormData);
		setFormResetSignal((signal) => signal + 1);
		bulkUpload.reset();
		if (mode === 'bulk') {
			bulkUpload.addRow();
		} else {
			setMode(isSeated ? 'seats' : 'one');
		}
	};

	useEffect(() => {
		afterSendRef.current = handleSendSuccess;
	});

	// The two modes hold separate in-flight flags, so without this the mode toggle is a way to reach a
	// second, enabled Send button while the first send is still running — two comps from one sitting.
	const anySendInFlight = isSending || bulkUpload.isSending;

	const recentComps = useRecentComps(id);
	const recentCompRows = (recentComps.data ?? []).map((comp) => ({
		...comp,
		justSent: justSentIds.includes(comp.transactionId),
	}));

	// Seated events expose ONLY chart selection — the comp API has no quantity/auto-assign path for
	// seated tickets, so the former "Single" (mode='one') option is not offered (it would 400). With a
	// single option the toggle itself is suppressed (showModeToggle below) — the chart form is the page.
	const modeOptions = isSeated
		? [
				{
					value: 'seats',
					label: t('send-tickets-mode-seats', 'Seat selection'),
					description: t('send-tickets-mode-seats-desc', 'Pick exact seats on the chart for one guest.'),
				},
		  ]
		: [
				{
					value: 'one',
					label: t('send-tickets-mode-one', 'Send to a guest'),
					description: t('send-tickets-mode-one-desc', "Enter the guest's name, email and choose their tickets."),
				},
				{
					value: 'bulk',
					label: t('send-tickets-mode-bulk', 'Send to multiple guests'),
					description: t(
						'send-tickets-mode-bulk-desc',
						'Add several guests in a table; optionally fill it from a CSV or Excel file.'
					),
				},
		  ];

	// Show the method toggle only when there is a real choice to make. Seated events resolve to a single
	// option (chart selection), so the toggle is hidden and the chart form stands alone.
	const showModeToggle = !!event && modeOptions.length > 1;

	const tileExceededTypeIds =
		mode === 'bulk' ? new Set(bulkUpload.getExceededTicketTypes().map((tt) => tt.iD)) : undefined;

	// Live per-pool over-reach for the red ring on the pool tiles — same budget + helpers as the on-send
	// gate (single source). Derived from the ACTIVE mode's input: bulk rows (id join) or the watched
	// recipients (name join). In single mode the stepper clamp keeps requests within the pool, so this
	// stays empty; it lights up in bulk mode, where per-row steppers have no pool-aware cap.
	const exceededPoolIds = !hasPools
		? undefined
		: mode === 'bulk'
		? exceededPoolIdSet(poolItemsFromBulkRows(bulkUpload.editableRows, poolBudget), poolBudget)
		: exceededPoolIdSet(poolItemsFromRecipients(recipientData, poolBudget), poolBudget);

	const leftColumnHeader = (
		<>
			{event && (
				<PunchupSendTicketsTitleSection title={event.name} days={event.days} streetAddress={event.streetAddress} />
			)}
			{hasPools && (
				<div className={styles.sectionGroup}>
					<div className={styles.sectionHeader}>
						<h3 className={styles.sectionTitle}>{t('send-tickets-pools-title', 'Ticket pools')}</h3>
						<p className={styles.sectionDescription}>
							{t(
								'send-tickets-pools-desc',
								'Shared capacity across the tiers below — sends draw down the pool, not each tier on its own.'
							)}
						</p>
					</div>
					<SendTicketsPoolTiles pools={poolBudget.pools} exceededPoolIds={exceededPoolIds} />
				</div>
			)}
			{bulkUpload.ticketTypes.length > 0 && (
				<div className={styles.sectionGroup}>
					<div className={styles.sectionHeader}>
						<h3 className={styles.sectionTitle}>{t('send-tickets-availability-title', 'Ticket availability')}</h3>
						<p className={styles.sectionDescription}>
							{t('send-tickets-availability-desc', 'Remaining inventory per ticket type.')}
						</p>
					</div>
					<TicketAvailabilityTiles ticketTypes={bulkUpload.ticketTypes} exceededTypeIds={tileExceededTypeIds} />
				</div>
			)}
			{showModeToggle && (
				<div className={styles.sectionGroup}>
					<div className={styles.sectionHeader}>
						<h3 className={styles.sectionTitle}>{t('send-tickets-method-title', 'Sending method')}</h3>
						<p className={styles.sectionDescription}>
							{/* Non-seated only — seated has no method choice, so the toggle (and this header) is hidden. */}
							{t(
								'send-tickets-method-desc',
								'Send directly to one guest, or build a list to comp several guests at once.'
							)}
						</p>
					</div>
					<PunchupRadioCards
						value={mode}
						onChange={(v) => {
							if (anySendInFlight) return;
							const next = v as SendMode;
							setMode(next);
							// Start bulk mode with one empty row so the editable table is
							// usable without first uploading a file (the file just fills it).
							if (next === 'bulk' && !bulkUpload.fileData && bulkUpload.editableRows.length === 0) {
								bulkUpload.addRow();
							}
						}}
						options={modeOptions}
					/>
				</div>
			)}
		</>
	);

	if (isError) {
		return (
			<PunchupPageShell
				pageName="Send Tickets"
				parentPageName="Comped Tickets"
				parentRoute={ROUTES.COMPED_TICKETS.replace(':id', id)}
			>
				<PunchupPageContent>
					<PunchupErrorState
						title={t('send-tickets-load-error-title', "We couldn't load this event")}
						description={t(
							'send-tickets-load-error-desc',
							'Something went wrong fetching the event details. Try again.'
						)}
						onRetry={() => refetch()}
					/>
				</PunchupPageContent>
			</PunchupPageShell>
		);
	}

	return (
		<PunchupPageShell
			pageName="Send Tickets"
			parentPageName="Comped Tickets"
			parentRoute={ROUTES.COMPED_TICKETS.replace(':id', id)}
		>
			<PunchupPageContent>
				<PunchupPageHeader title="Send Tickets" description="Send complimentary tickets directly to a guest." />

				{mode === 'bulk' ? (
					<div className={styles.formContainer}>
						<div className={styles.leftColumn}>
							{leftColumnHeader}
							<PunchupBulkUploadPanel
								uploadState={bulkUpload}
								poolBudget={hasPools ? poolBudget : undefined}
								exceededPoolIds={exceededPoolIds}
							/>
						</div>
						<div className={styles.confirmationColumn}>
							<PunchupBulkConfirmation
								rows={bulkUpload.editableRows.map((row) => {
									const tt = bulkUpload.ticketTypes.find((type) => type.iD === row.ticketTypeId);
									return {
										email: row.email,
										quantity: row.quantity,
										ticketTypeName: tt?.name ?? null,
										unitPrice: tt?.initialPrice ?? null,
									};
								})}
								isSending={bulkUpload.isSending}
								onSend={() => {
									if (anySendInFlight) return;
									const exceededPools = hasPools
										? exceededPoolsFor(poolItemsFromBulkRows(bulkUpload.editableRows, poolBudget), poolBudget)
										: [];
									const error = validateBulkBeforeSend(
										bulkUpload.editableRows,
										bulkUpload.getExceededTicketTypes(),
										t,
										exceededPools
									);
									if (error) {
										postToast('error', error);
										return;
									}
									bulkUpload.handleSendTickets();
								}}
								onCancel={goToCompedTickets}
							/>
							<SendTicketsRecentComps
								comps={recentCompRows}
								isLoading={recentComps.isLoading}
								isError={recentComps.isError}
								onRetry={recentComps.refetch}
							/>
						</div>
					</div>
				) : (
					<Form
						className={styles.formContainer}
						onSubmit={(data) => {
							if (formSendDisabled) {
								postToast('error', t('send-tickets-error-no-quantity', 'Please select at least one ticket.'));
								return;
							}
							if (hasPools) {
								const exceededPools = exceededPoolsFor(
									poolItemsFromRecipients(data.recipients, poolBudget),
									poolBudget
								);
								if (exceededPools.length > 0) {
									postToast('error', poolExceededMessage(exceededPools, t));
									return;
								}
							}
							submitHandler(data, mutateCheckAvailability);
						}}
						formMethods={formMethods}
					>
						<div className={styles.leftColumn}>
							{leftColumnHeader}
							{mode === 'seats' && event ? (
								<PunchupSendTicketsFormSeated event={event} resetSignal={formResetSignal} />
							) : (
								<PunchupSendTicketsForm
									key={formResetSignal}
									event={event}
									poolBudget={hasPools ? poolBudget : undefined}
								/>
							)}
						</div>
						<div className={styles.confirmationColumn}>
							<PunchupSendTicketsConfirmation
								onCancel={goToCompedTickets}
								totalPrice={totalPrice}
								isSending={isSending}
							>
								{recipientData.map((el: RecipientProps, i: number) => (
									<PunchupRecipientInfo key={i} {...el} recipientId={i} />
								))}
							</PunchupSendTicketsConfirmation>
							<SendTicketsRecentComps
								comps={recentCompRows}
								isLoading={recentComps.isLoading}
								isError={recentComps.isError}
								onRetry={recentComps.refetch}
							/>
						</div>
					</Form>
				)}
			</PunchupPageContent>

			{isErrorModalOpen && <PunchupErrorModal closeModal={closeErrorModal} />}
		</PunchupPageShell>
	);
};
